import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { collection, getDocs, getFirestore } from 'firebase/firestore';
import firebaseApp from '../../util/firebase';
import pipelineMapData from '../../data/pipelineMap.json';
import demoPipelineMapData from '../../data/demoPipelineMap.json';
import StationTooltip from './StationTooltip';
import StationBatchModal from './StationBatchModal';

const SCALE_FACTOR = 1.2;
const MIN_SCALE = 0.5;
const MAX_SCALE = 3;

function PipelineMap() {
  // Select the appropriate pipeline data based on hostname
  const pipelineData = useMemo(() => {
    if (
      typeof window !== 'undefined' &&
      (window.location.hostname.match(/colpipe\./) || window.location.hostname.match(/localhost/))
    ) {
      return pipelineMapData;
    }
    return demoPipelineMapData;
  }, []);

  const [hoveredStation, setHoveredStation] = useState(null);
  const [selectedStation, setSelectedStation] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [translateX, setTranslateX] = useState(0);
  const [translateY, setTranslateY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [batchData, setBatchData] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(true);
  const svgRef = useRef(null);

  const calculateViewBox = useCallback(() => {
    if (!pipelineData?.stations) return '0 0 2700 500';

    const stations = Object.values(pipelineData.stations);
    const positions = stations.map((s) => s.position);

    const minX = Math.min(...positions.map((p) => p.x)) - 100;
    const maxX = Math.max(...positions.map((p) => p.x)) + 100;
    const minY = Math.min(...positions.map((p) => p.y)) - 50;
    const maxY = Math.max(...positions.map((p) => p.y)) + 50;

    const width = maxX - minX;
    const height = maxY - minY;

    return `${minX} ${minY} ${width} ${height}`;
  }, [pipelineData]);

  const viewBox = calculateViewBox();

  const loadBatchData = useCallback(async () => {
    try {
      setLoadingBatches(true);

      // Get Firestore instance with the initialized app
      const db = getFirestore(firebaseApp);
      const batchesRef = collection(db, 'batches');
      const querySnapshot = await getDocs(batchesRef);

      const batches = [];
      if (querySnapshot && querySnapshot.forEach) {
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          batches.push({ id: doc.id, ...data });
        });
      }

      setBatchData(batches);
    } catch (error) {
      // Only log non-Firebase initialization errors
      if (error.code !== 'app/no-app' && process.env.NODE_ENV !== 'test') {
        console.error('Error loading batch data:', error);
      }
    } finally {
      setLoadingBatches(false);
    }
  }, []);

  useEffect(() => {
    loadBatchData();
  }, [loadBatchData]);

  const handleStationMouseEnter = useCallback((station, event) => {
    setHoveredStation(station);

    // Use screen coordinates for tooltip positioning
    setTooltipPosition({
      x: event.clientX,
      y: event.clientY - 30
    });
  }, []);

  const handleStationMouseLeave = () => {
    setHoveredStation(null);
  };

  const handleStationClick = (station) => {
    // Show modal for all stations
    setSelectedStation(station);
  };

  const resetView = useCallback(() => {
    // Calculate optimal scale to fit all content
    if (pipelineData?.stations && svgRef.current) {
      const stations = Object.values(pipelineData.stations);
      const positions = stations.map((s) => s.position);

      const minX = Math.min(...positions.map((p) => p.x));
      const maxX = Math.max(...positions.map((p) => p.x));
      const minY = Math.min(...positions.map((p) => p.y));
      const maxY = Math.max(...positions.map((p) => p.y));

      const contentWidth = maxX - minX;
      const contentHeight = maxY - minY;

      // Get container dimensions
      const container = svgRef.current.parentElement;
      if (container) {
        const containerRect = container.getBoundingClientRect();
        const containerWidth = containerRect.width;
        const containerHeight = containerRect.height;

        // Calculate scale to fit content with some padding
        const scaleX = (containerWidth * 0.85) / contentWidth;
        const scaleY = (containerHeight * 0.85) / contentHeight;
        const optimalScale = Math.min(scaleX, scaleY, MAX_SCALE);

        // Ensure we don't go below minimum scale
        const finalScale = Math.max(optimalScale, MIN_SCALE);

        setScale(finalScale);
        setTranslateX(0);
        setTranslateY(0);

        // Trigger reset animation using React state
        setIsResetting(true);
        setTimeout(() => setIsResetting(false), 500);
        return;
      }
    }

    // Fallback to scale 1 if calculation fails
    setScale(1);
    setTranslateX(0);
    setTranslateY(0);
  }, [pipelineData]);

  // Auto-center content on mount and when data changes
  useEffect(() => {
    // Small delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      resetView();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [resetView]);

  // Handle window resize to maintain fit-to-viewport
  useEffect(() => {
    let resizeTimeoutId;

    const handleResize = () => {
      // Clear existing timeout
      clearTimeout(resizeTimeoutId);
      // Debounce resize events
      resizeTimeoutId = setTimeout(() => {
        resetView();
      }, 250);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeoutId);
    };
  }, [resetView]);

  const handleWheel = (event) => {
    event.preventDefault();
    const delta = event.deltaY > 0 ? 1 / SCALE_FACTOR : SCALE_FACTOR;
    const newScale = Math.min(Math.max(scale * delta, MIN_SCALE), MAX_SCALE);
    setScale(newScale);
  };

  const handleMouseDown = (event) => {
    if (event.target === svgRef.current || event.target.parentNode === svgRef.current) {
      setIsDragging(true);
      setDragStart({
        x: event.clientX - translateX,
        y: event.clientY - translateY
      });
    }
  };

  const handleMouseMove = useCallback(
    (event) => {
      if (isDragging && dragStart) {
        const newTranslateX = event.clientX - dragStart.x;
        const newTranslateY = event.clientY - dragStart.y;

        // Add boundary limits based on container size and scale
        const containerRect = svgRef.current?.parentElement?.getBoundingClientRect();
        if (containerRect) {
          const maxTranslateX = containerRect.width * 0.5;
          const minTranslateX = -containerRect.width * 0.5;
          const maxTranslateY = containerRect.height * 0.5;
          const minTranslateY = -containerRect.height * 0.5;

          setTranslateX(Math.min(maxTranslateX, Math.max(minTranslateX, newTranslateX)));
          setTranslateY(Math.min(maxTranslateY, Math.max(minTranslateY, newTranslateY)));
        } else {
          setTranslateX(newTranslateX);
          setTranslateY(newTranslateY);
        }
      }
    },
    [isDragging, dragStart]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchStart = useCallback(
    (event) => {
      if (event.touches.length === 1) {
        const touch = event.touches[0];
        setIsDragging(true);
        setDragStart({
          x: touch.clientX - translateX,
          y: touch.clientY - translateY
        });
      }
    },
    [translateX, translateY]
  );

  const handleTouchMove = useCallback(
    (event) => {
      if (event.touches.length === 1 && isDragging && dragStart) {
        event.preventDefault();
        const touch = event.touches[0];
        handleMouseMove({ clientX: touch.clientX, clientY: touch.clientY });
      }
    },
    [isDragging, dragStart, handleMouseMove]
  );

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  const svgStyle = useMemo(
    () => ({
      cursor: isDragging ? 'grabbing' : 'grab',
      // Fix: translate first, then scale
      transform: `translate(${translateX}px, ${translateY}px) scale(${scale})`,
      transformOrigin: 'center',
      transition: isDragging
        ? 'none'
        : isResetting
          ? 'transform 0.5s ease-out'
          : 'transform 0.3s ease'
    }),
    [isDragging, scale, translateX, translateY, isResetting]
  );

  const renderConnection = (connection, index) => {
    const fromStation = pipelineData.stations[connection.from];
    const toStation = pipelineData.stations[connection.to];
    const line = pipelineData.lines.find((l) => l.id === connection.line);

    if (!fromStation || !toStation || !line) return null;

    return (
      <line
        key={`connection-${index}`}
        x1={fromStation.position.x}
        y1={fromStation.position.y}
        x2={toStation.position.x}
        y2={toStation.position.y}
        stroke={line.color}
        strokeWidth="6"
        className="pipeline-connection"
      />
    );
  };

  const renderStation = (stationId) => {
    const station = pipelineData.stations[stationId];
    if (!station) return null;

    const isHovered = hoveredStation?.id === station.id;
    const isSelected = selectedStation?.id === station.id;

    let radius = 6;
    let strokeWidth = 2;

    if (station.type === 'terminal') {
      radius = 8;
      strokeWidth = 3;
    } else if (station.type === 'junction') {
      radius = 7;
    }

    return (
      <g key={station.id}>
        <circle
          cx={station.position.x}
          cy={station.position.y}
          r={radius}
          fill={isSelected ? '#ffc107' : '#ffffff'}
          stroke={isHovered ? '#0d6efd' : '#000000'}
          strokeWidth={strokeWidth}
          className={`pipeline-station ${station.type}`}
          onMouseEnter={(e) => handleStationMouseEnter(station, e)}
          onMouseLeave={handleStationMouseLeave}
          onClick={() => handleStationClick(station)}
          style={{ cursor: 'pointer' }}
        />
        {(() => {
          // Use labelRotation from station data if available
          if (station.labelRotation !== undefined) {
            // Apply the rotation from the station data
            const xOffset = 10;
            const yOffset = -10;
            return (
              <text
                x={station.position.x + xOffset}
                y={station.position.y + yOffset}
                textAnchor="start"
                className="station-label"
                fontSize="12"
                fontWeight="bold"
                fill="#000000"
                pointerEvents="none"
                transform={`rotate(${station.labelRotation} ${station.position.x + xOffset} ${station.position.y + yOffset})`}
              >
                {station.name}
              </text>
            );
          } else {
            // Left or right positioning for stations without rotation
            const isOnRightSide = station.position.x > 700;
            const xOffset = isOnRightSide ? -10 : 10;
            const anchor = isOnRightSide ? 'end' : 'start';

            return (
              <text
                x={station.position.x + xOffset}
                y={station.position.y - 5}
                textAnchor={anchor}
                className="station-label"
                fontSize="12"
                fontWeight="bold"
                fill="#000000"
                pointerEvents="none"
              >
                {station.name}
              </text>
            );
          }
        })()}
      </g>
    );
  };

  return (
    <div className="pipeline-map-wrapper">
      <div className="map-controls">
        <button onClick={() => setScale(Math.min(scale * SCALE_FACTOR, MAX_SCALE))}>Zoom In</button>
        <button onClick={() => setScale(Math.max(scale / SCALE_FACTOR, MIN_SCALE))}>
          Zoom Out
        </button>
        <button onClick={resetView}>Reset</button>
        <button onClick={loadBatchData} disabled={loadingBatches}>
          {loadingBatches ? 'Loading...' : 'Refresh Data'}
        </button>
      </div>

      <svg
        ref={svgRef}
        viewBox={viewBox}
        className="pipeline-svg"
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        style={svgStyle}
      >
        <g className="pipeline-connections">
          {pipelineData.connections.map((connection, index) => renderConnection(connection, index))}
        </g>

        <g className="pipeline-stations">
          {Object.keys(pipelineData.stations).map((stationId) => renderStation(stationId))}
        </g>
      </svg>

      {hoveredStation && (
        <StationTooltip
          station={hoveredStation}
          position={tooltipPosition}
          scale={scale}
          batchData={batchData}
        />
      )}

      {selectedStation && (
        <StationBatchModal
          station={selectedStation}
          position={{ x: window.innerWidth / 2, y: window.innerHeight / 2 }}
          onClose={() => setSelectedStation(null)}
          batchData={batchData}
        />
      )}
    </div>
  );
}

export default PipelineMap;
